import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { Minus, Plus, Trash2, ShoppingBag, Tag } from "lucide-react";
import { useCart } from "../contexts/CartContext";
import { formatPrice, FREE_SHIPPING_THRESHOLD, resolveImg, applyCouponLocal } from "../lib/api";
import CouponInput from "../components/CouponInput";
import StockNotes from "../components/StockNotes";

export default function Cart() {
  const { items, updateQty, removeItem, subtotal, coupon, refreshStock } = useCart();
  const { discount, shipping, total } = applyCouponLocal(subtotal, coupon);
  const remaining = Math.max(0, FREE_SHIPPING_THRESHOLD - subtotal);

  useEffect(() => { refreshStock(); }, [refreshStock]);

  if (items.length === 0) {
    return (
      <div className="max-w-3xl mx-auto px-5 py-32 text-center" data-testid="empty-cart">
        <StockNotes className="mb-8 text-left" />
        <ShoppingBag className="w-16 h-16 text-[var(--sage)] mx-auto mb-6" />
        <h1 className="font-serif text-4xl text-[var(--ink-forest)]">Your basket is empty</h1>
        <p className="mt-3 text-[var(--muted-fg)]">Discover oils, gifts, and treasures.</p>
        <Link
          to="/shop"
          className="mt-8 inline-block bg-[var(--terracotta)] text-white rounded-full px-8 py-3 text-sm font-semibold hover:bg-[var(--terracotta-deep)] transition-colors"
          data-testid="empty-cart-shop-btn"
        >
          Start shopping
        </Link>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-5 sm:px-8 lg:px-12 py-12 lg:py-20" data-testid="cart-page">
      <p className="eyebrow mb-3">Basket</p>
      <h1 className="font-serif text-5xl text-[var(--ink-forest)] tracking-tight mb-6">
        Your selections
      </h1>

      {!coupon && (
        <a
          href="#cart-coupon-input"
          onClick={(e) => {
            e.preventDefault();
            const el = document.getElementById("cart-coupon-input");
            if (el) {
              el.scrollIntoView({ behavior: "smooth", block: "center" });
              setTimeout(() => el.querySelector("input")?.focus(), 400);
            }
          }}
          className="group inline-flex items-center gap-2.5 mb-10 px-4 py-2.5 rounded-full bg-[var(--muted)]/60 border border-dashed border-[var(--terracotta)]/60 text-sm text-[var(--ink-forest)] hover:bg-[var(--muted)] hover:border-[var(--terracotta)] transition-all"
          data-testid="have-coupon-ribbon"
        >
          <Tag className="w-3.5 h-3.5 text-[var(--terracotta)] group-hover:rotate-12 transition-transform" />
          <span>
            Have a coupon?
            <span className="text-[var(--terracotta)] font-medium ml-1.5 underline underline-offset-2 decoration-dotted">
              Apply it here →
            </span>
          </span>
        </a>
      )}

      <div className="grid lg:grid-cols-3 gap-10">
        <div className="lg:col-span-2 space-y-4">
          <StockNotes />
          {items.map((item) => (
            <div
              key={item.product_id}
              className="flex gap-5 p-5 bg-[var(--bg-surface)] border border-[var(--border)] rounded-2xl"
              data-testid={`cart-row-${item.product_id}`}
            >
              <Link to={`/product/${item.product_id}`} className="shrink-0">
                <img
                  src={resolveImg(item.image_url)}
                  alt={item.name}
                  className="w-24 h-24 sm:w-28 sm:h-28 object-cover rounded-xl bg-[var(--muted)]"
                />
              </Link>
              <div className="flex-1 min-w-0">
                <Link
                  to={`/product/${item.product_id}`}
                  className="font-serif text-lg sm:text-xl text-[var(--ink-forest)] hover:text-[var(--terracotta)]"
                >
                  {item.name}
                </Link>
                <p className="text-sm text-[var(--muted-fg)] mt-1">{formatPrice(item.price)} each</p>
                <div className="mt-3 flex items-center gap-4 flex-wrap">
                  <div className="inline-flex items-center border border-[var(--border)] rounded-full">
                    <button
                      onClick={() => updateQty(item.product_id, item.quantity - 1)}
                      className="p-2 hover:bg-[var(--muted)] rounded-full"
                      data-testid={`cart-decrease-${item.product_id}`}
                    >
                      <Minus className="w-3.5 h-3.5" />
                    </button>
                    <span className="px-4 text-sm font-medium">{item.quantity}</span>
                    <button
                      onClick={() => updateQty(item.product_id, item.quantity + 1)}
                      disabled={typeof item.stock === "number" && item.quantity >= item.stock}
                      className="p-2 hover:bg-[var(--muted)] rounded-full disabled:opacity-30 disabled:hover:bg-transparent"
                      data-testid={`cart-increase-${item.product_id}`}
                    >
                      <Plus className="w-3.5 h-3.5" />
                    </button>
                  </div>
                  {typeof item.stock === "number" && item.quantity >= item.stock && item.stock <= 5 && (
                    <span className="text-xs text-[var(--terracotta)]" data-testid={`cart-max-${item.product_id}`}>Only {item.stock} available</span>
                  )}
                  <button
                    onClick={() => removeItem(item.product_id)}
                    className="text-xs text-[var(--muted-fg)] hover:text-[var(--terracotta)] flex items-center gap-1"
                    data-testid={`cart-remove-${item.product_id}`}
                  >
                    <Trash2 className="w-3.5 h-3.5" /> Remove
                  </button>
                </div>
              </div>
              <p className="font-semibold text-[var(--ink-forest)] whitespace-nowrap">
                {formatPrice(item.price * item.quantity)}
              </p>
            </div>
          ))}
        </div>

        {/* Summary */}
        <div className="lg:sticky lg:top-28 self-start bg-[var(--bg-surface)] border border-[var(--border)] rounded-2xl p-7" data-testid="cart-summary">
          <h2 className="font-serif text-2xl text-[var(--ink-forest)] mb-5">Summary</h2>

          {remaining > 0 && (
            <div className="mb-5 text-xs text-[var(--ink-forest)] bg-[var(--muted)]/50 rounded-lg p-3">
              Add <span className="font-semibold text-[var(--terracotta)]">{formatPrice(remaining)}</span> more for free shipping.
            </div>
          )}

          <div className="space-y-3 text-sm">
            <Row label="Subtotal" value={formatPrice(subtotal)} dataTestid="summary-subtotal" />
            {discount > 0 && (
              <Row
                label={`Coupon (${coupon.code})`}
                value={`−${formatPrice(discount)}`}
                valueClass="text-[var(--terracotta)] font-semibold"
                dataTestid="summary-discount"
              />
            )}
            <Row
              label="Shipping"
              value={shipping === 0 ? "Free" : formatPrice(shipping)}
              valueClass={shipping === 0 ? "text-[var(--terracotta)] font-semibold" : ""}
              dataTestid="summary-shipping"
            />
            <div className="border-t border-[var(--border)] my-3" />
            <Row
              label="Total"
              value={formatPrice(total)}
              labelClass="text-base font-semibold"
              valueClass="text-base font-bold text-[var(--ink-forest)]"
              dataTestid="summary-total"
            />
          </div>

          <div className="mt-5" id="cart-coupon-input">
            <CouponInput />
          </div>

          <Link
            to="/checkout"
            className="mt-7 block text-center w-full bg-[var(--terracotta)] text-white rounded-full py-3.5 text-sm font-semibold hover:bg-[var(--terracotta-deep)] transition-colors"
            data-testid="checkout-btn"
          >
            Proceed to checkout
          </Link>
          <Link
            to="/shop"
            className="mt-3 block text-center w-full border border-[var(--border)] text-[var(--ink-forest)] rounded-full py-3 text-sm hover:bg-[var(--muted)] transition-colors"
          >
            Keep shopping
          </Link>
        </div>
      </div>
    </div>
  );
}

function Row({ label, value, labelClass = "", valueClass = "", dataTestid }) {
  return (
    <div className="flex justify-between" data-testid={dataTestid}>
      <span className={`text-[var(--muted-fg)] ${labelClass}`}>{label}</span>
      <span className={`text-[var(--ink-forest)] ${valueClass}`}>{value}</span>
    </div>
  );
}
